export const MY_MEMBERSHIPS_PAGE_SIZE = 10;

export const MY_MEMBERSHIPS_COLUMN_DEFS = [
  { id: "membership_id", label: "Membership ID", defaultVisible: true },
  { id: "plan_name", label: "Plan", defaultVisible: true },
  { id: "source", label: "Source", defaultVisible: true },
  { id: "start_date", label: "Start Date", defaultVisible: true },
  { id: "end_date", label: "End Date", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "payment_amount", label: "Amount", defaultVisible: true },
  { id: "payment_status", label: "Payment", defaultVisible: true },
];

export const MY_MEMBERSHIPS_VISIBILITY_KEY =
  "girinestham.myMemberships.directory.columnVisibility";

export function defaultMyMembershipsFilters() {
  return { search: "", column: "", value: "" };
}

export function formatMembershipDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export function formatMembershipAmount(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `₹${n.toFixed(2)}`;
  }
}

export function membershipStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "mm-status-badge mm-status-badge--active";
  if (s === "expired") return "mm-status-badge mm-status-badge--expired";
  if (s === "pending") return "mm-status-badge mm-status-badge--pending";
  return "mm-status-badge mm-status-badge--muted";
}

export function paginateList(items, page, pageSize = MY_MEMBERSHIPS_PAGE_SIZE) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    totalPages,
    total,
    slice: items.slice(start, start + pageSize),
  };
}

export function timelineCellValue(colId, row) {
  switch (colId) {
    case "membership_id":
      return row.membership_id || "—";
    case "plan_name":
      return row.plan_name || row.membership_type || "—";
    case "source":
      return row.source === "renewal" ? "Renewal" : "Application";
    case "start_date":
    case "end_date":
    case "payment_date":
      return formatMembershipDate(row[colId]);
    case "status":
      return row.status || "—";
    case "payment_amount":
      return formatMembershipAmount(
        row.payment_amount ?? row.amount ?? row.membership_fee ?? row.plan_price
      );
    case "payment_status": {
      if (row.payment_status && row.payment_status !== "No payment") {
        return row.payment_status;
      }
      if (row.payment_id != null || row.payment_amount != null || row.amount != null) {
        return "Paid";
      }
      return "No payment";
    }
    case "payment_verified":
      if (row.payment_verified == null) return "—";
      return row.payment_verified ? "Verified" : "Not verified";
    case "receipt_no":
      return row.receipt_no || "—";
    case "transaction_id":
      return row.transaction_id || "—";
    case "payment_mode":
      return row.payment_mode || "—";
    default:
      return row?.[colId] ?? "—";
  }
}

export function timelineSearchableText(row) {
  return [
    row.membership_id,
    row.plan_name,
    row.membership_type,
    row.source,
    row.status,
    row.payment_status,
    row.receipt_no,
    row.transaction_id,
    row.payment_mode,
    row.remarks,
  ]
    .filter(Boolean)
    .join(" ");
}

export function filterTimelineRows(rows, filters) {
  let result = Array.isArray(rows) ? [...rows] : [];
  const search = String(filters?.search ?? "")
    .trim()
    .toLowerCase();
  if (search) {
    result = result.filter((row) =>
      timelineSearchableText(row).toLowerCase().includes(search)
    );
  }
  const col = String(filters?.column ?? "").trim();
  const val = String(filters?.value ?? "").trim();
  if (col && val) {
    result = result.filter((row) => {
      const cell = timelineCellValue(col, row);
      return String(cell ?? "").trim().toLowerCase() === val.toLowerCase();
    });
  }
  return result;
}

export function buildValueOptionsFromRows(rows, columnId) {
  const seen = new Set();
  const opts = [];
  for (const row of rows || []) {
    const raw = timelineCellValue(columnId, row);
    if (raw == null || raw === "" || raw === "—") continue;
    const label = String(raw);
    if (!seen.has(label)) {
      seen.add(label);
      opts.push({ value: label, label });
    }
  }
  return opts.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
  );
}

export function loadColumnVisibility(storageKey, columnDefs) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialColumnVisibility(storageKey, columnDefs) {
  const saved = loadColumnVisibility(storageKey, columnDefs);
  const initial = {};
  for (const c of columnDefs) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

export function membershipDaysProgress(startDate, endDate) {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { percent: 0, daysLeft: 0, totalDays: 0 };
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const totalMs = end.getTime() - start.getTime();
  const totalDays = Math.max(1, Math.ceil(totalMs / (1000 * 60 * 60 * 24)));
  const elapsedMs = today.getTime() - start.getTime();
  const percent = Math.min(100, Math.max(0, Math.round((elapsedMs / totalMs) * 100)));
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  return { percent, daysLeft, totalDays };
}
